import { useMemo, useState } from 'react';
import type { OperatorBuilding, OperatorDetail } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { HonestyBadge } from '../../ui/HonestyBadge';

// The ownership network, ported from production's relationship network
// (relationship_network_view.dart + widgets/relationship_graph.dart) and drawn in the V3
// design's colours. It opens inside an operator's panel on North Star > Operators: no
// page of its own. Three lenses as production had them: Graph (the operator in the
// middle, its buildings above sized by beds and coloured by score, its CMS backers on an
// inner ring), Supply Chain and Geography (beds by state), with a rail that quantifies
// the focal or the inspected building.
//
// Every node and edge is a row the server sent: a building is a catalog row whose CMS
// owner of record is this operator; a backer is a CMS owner row (an organization with an
// ownership or control role on its buildings). Production filled the unwired arms with a
// SAMPLE corpus behind a toggle; this port does not: an arm with no feed is drawn as a
// named empty slot that says what would fill it, never a made-up partner.

const INK = '#22272E';
const INK2 = '#3C454F';
const INK3 = '#54606C';
const GREY = '#5B6B7C';
const BLUE = '#2F5C97';
const GREEN = '#1F6B52';
const AMBER = '#A04A0C';
const RED = '#B3261E';
const LINE = 'rgba(34,39,46,.14)';
const CAP = 'font-size:14px;letter-spacing:.09em;text-transform:uppercase;color:#54606C;font-weight:600;';

const num = (v: unknown) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const n0 = (v: number | null | undefined) => (v == null ? '–' : Math.round(v).toLocaleString('en-US'));

export type Lens = 'Graph' | 'Supply Chain' | 'Geography';
type Cluster = 'Buildings' | 'Backers' | 'Suppliers' | 'Demand' | 'Referrals' | 'Peers';

/** What would fill each arm that has no feed (production's waitingOn). */
export const WAITING_ON: Record<'Suppliers' | 'Demand' | 'Referrals' | 'Peers', string> = {
  Suppliers: 'EHR, pharmacy, lab, agency staffing, therapy',
  Demand: 'referring hospitals, physician groups, payer mix',
  Referrals: 'discharge planners, placement agencies, ACOs',
  Peers: 'buildings competing for the same census',
};

/** The most buildings drawn on the graph; the rest are listed in the panel. */
export const GRAPH_MAX = 60;

/** Colour by composite on the console's ramp: grey is not scored, never "healthy". */
export function scoreColor(composite: number | null): string {
  if (composite == null) return GREY;
  return composite < 50 ? RED : composite < 70 ? AMBER : GREEN;
}

/** Herfindahl index of beds by state (0 to 10,000), null with no state on any row. */
export function hhiOf(facilities: OperatorBuilding[]): number | null {
  const by = new Map<string, number>();
  for (const f of facilities) if (f.state) by.set(f.state, (by.get(f.state) ?? 0) + (num(f.beds) ?? 0));
  const total = [...by.values()].reduce((a, b) => a + b, 0);
  if (!by.size || total <= 0) return null;
  return Math.round([...by.values()].reduce((s, b) => s + (b / total) ** 2, 0) * 10000);
}

interface GNode { id: string; kind: 'building' | 'backer'; x: number; y: number; r: number; label: string; fill: string; ring: boolean; flag: boolean; title: string }
interface Ghost { cluster: Cluster; x: number; y: number; note: string }

const W = 760;
const H = 430;
const CX = W / 2;
const CY = H / 2 + 20;
const RX = Math.max(110, (W / 2 - 120) / 1.12);
const RY = H * 0.36;

/** Production's arc: each arm keeps to its quadrant; the sweep widens with the count. */
function arc(centre: number, n: number): [number, number] {
  const span = Math.PI * (0.26 + 0.2 * Math.min(1, (n - 1) / 9));
  return [centre - span / 2, centre + span / 2];
}
const UP = -Math.PI / 2;
const LEFT = Math.PI;
const RIGHT = 0;

/** The deterministic layout (production settles the same slots with a spring; this draws the slots). */
export function layout(d: OperatorDetail, on: Set<Cluster>): { nodes: GNode[]; ghosts: Ghost[]; drawn: number } {
  const nodes: GNode[] = [];
  const ghosts: Ghost[] = [];
  const ghost = (cluster: Cluster, centre: number, note: string) =>
    ghosts.push({ cluster, x: CX + Math.cos(centre) * RX * 0.94, y: CY + Math.sin(centre) * RY * 0.94, note });
  const blds = on.has('Buildings') ? [...d.facilities].sort((a, b) => (num(b.beds) ?? 0) - (num(a.beds) ?? 0)).slice(0, GRAPH_MAX) : [];
  if (on.has('Buildings') && !blds.length) ghost('Buildings', UP, 'no building on record');
  const maxBeds = Math.max(1, ...blds.map((f) => num(f.beds) ?? 0));
  const [a0, a1] = arc(UP, blds.length);
  const lanes = Math.min(6, Math.max(3, Math.ceil(blds.length / 4)));
  blds.forEach((f, i) => {
    const n = blds.length;
    const ang = n === 1 ? (a0 + a1) / 2 : a0 + (a1 - a0) * (i / (n - 1));
    const m = 0.62 + (i % lanes) * (0.62 / (lanes - 1));
    const comp = num(f.composite_score);
    const beds = num(f.beds);
    nodes.push({
      id: f.id, kind: 'building',
      x: CX + Math.cos(ang) * RX * m, y: CY + Math.sin(ang) * RY * m,
      r: 6 + Math.min(10, Math.sqrt((beds ?? 0) / maxBeds) * 10),
      label: f.name, fill: scoreColor(comp), ring: f.pe_backed === true,
      flag: f.abuse_icon === true || (num(f.severe_deficiency_count) ?? 0) > 0,
      title: [f.name, [f.city, f.state].filter(Boolean).join(', '), beds != null ? beds + ' beds' : null, comp != null ? 'composite ' + Math.round(comp) : 'not scored'].filter(Boolean).join(' · '),
    });
  });
  if (on.has('Backers')) {
    const known = d.ownersAvailable !== false;
    const owners = known ? d.owners : [];
    if (!owners.length) ghost('Backers', LEFT + 0.55, known ? 'CMS lists no other organization' : 'not known: ' + (d.ownersReason ?? 'owner rows unavailable'));
    const maxB = Math.max(1, ...owners.map((o) => o.buildings));
    const [b0, b1] = arc(LEFT + 0.55, owners.length);
    owners.forEach((o, i) => {
      const n = owners.length;
      const ang = n === 1 ? (b0 + b1) / 2 : b0 + (b1 - b0) * (i / (n - 1));
      const m = 0.55 + (i % 2) * 0.3;
      nodes.push({
        id: 'owner:' + o.name, kind: 'backer',
        x: CX + Math.cos(ang) * RX * m, y: CY + Math.sin(ang) * RY * m,
        r: 6 + Math.min(8, Math.sqrt(o.buildings / maxB) * 8),
        label: o.name, fill: BLUE, ring: o.peLike, flag: false,
        title: `${o.name} · on ${o.buildings} of ${d.facilities.length} buildings${o.maxPct != null ? ` · up to ${o.maxPct}%` : ''}`,
      });
    });
  }
  if (on.has('Suppliers')) ghost('Suppliers', LEFT - 0.35, WAITING_ON.Suppliers);
  if (on.has('Demand')) ghost('Demand', RIGHT, WAITING_ON.Demand);
  if (on.has('Referrals') || on.has('Peers')) {
    const both = on.has('Referrals') && on.has('Peers');
    ghosts.push({
      cluster: both ? 'Referrals' : on.has('Referrals') ? 'Referrals' : 'Peers',
      x: CX, y: CY + RY * 0.94,
      note: both ? `${WAITING_ON.Referrals}; ${WAITING_ON.Peers}` : on.has('Referrals') ? WAITING_ON.Referrals : WAITING_ON.Peers,
    });
  }
  return { nodes, ghosts, drawn: blds.length };
}

const short = (s: string, n = 20) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

function Gauge({ title, value, word, color, rows }: { title: string; value: number | null; word: string; color: string; rows: [string, string][] }) {
  return (
    <div style={sx('display:flex;flex-direction:column;gap:5px;')}>
      <div style={sx('display:flex;align-items:baseline;gap:8px;')}>
        <span style={sx(CAP)}>{title}</span>
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:' + color + ';')}>{word}</span>
      </div>
      <div style={sx('height:5px;border-radius:3px;background:rgba(34,39,46,.07);overflow:hidden;')}>
        <div style={sx('height:5px;border-radius:3px;background:' + color + ';width:' + (value == null ? 0 : Math.max(2, Math.min(100, value))) + '%;')} />
      </div>
      {rows.map(([k, v]) => (
        <span key={k} style={sx('display:flex;justify-content:space-between;font-size:14px;color:' + INK2 + ';')}>
          <span>{k}</span>
          <span style={sx('font-weight:700;color:' + INK + ';font-variant-numeric:tabular-nums;')}>{v}</span>
        </span>
      ))}
    </div>
  );
}

export function OwnershipNetwork({ org, d, onDealDesk }: {
  org: string;
  d: OperatorDetail;
  /** Sends a building to the Deal Desk; resolves to a line to show, or null when it opened. */
  onDealDesk?: (f: OperatorBuilding) => Promise<string | null>;
}) {
  const [lens, setLens] = useState<Lens>('Graph');
  const [on, setOn] = useState<Set<Cluster>>(() => new Set<Cluster>(['Buildings', 'Backers', 'Suppliers', 'Demand', 'Referrals', 'Peers']));
  const [inspected, setInspected] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const g = useMemo(() => layout(d, on), [d, on]);
  const fac = d.facilities;
  const bedTotal = fac.reduce((s, f) => s + (num(f.beds) ?? 0), 0);
  const scored = fac.filter((f) => num(f.composite_score) != null);
  const distressed = scored.filter((f) => (num(f.composite_score) ?? 100) < 50).length;
  const distress = scored.length ? Math.round(100 - scored.reduce((s, f) => s + (num(f.composite_score) ?? 0), 0) / scored.length) : null;
  const pe = fac.filter((f) => f.pe_backed === true).length;
  const hhi = hhiOf(fac);
  const states = [...new Set(fac.map((f) => f.state).filter(Boolean))];
  const ownersKnown = d.ownersAvailable !== false;
  // Chip counts: null is NO FEED (a dash, never a zero).
  const count: Record<Cluster, number | null> = {
    Buildings: fac.length, Backers: ownersKnown ? d.owners.length : null, Suppliers: null, Demand: null, Referrals: null, Peers: null,
  };
  const live = (c: Cluster) => c === 'Buildings' || c === 'Backers';
  const entities = (on.has('Buildings') ? g.drawn : 0) + (on.has('Backers') && ownersKnown ? d.owners.length : 0);
  const sel = inspected ? fac.find((f) => f.id === inspected) ?? null : null;
  const selOwner = inspected?.startsWith('owner:') ? d.owners.find((o) => 'owner:' + o.name === inspected) ?? null : null;
  const send = async (f: OperatorBuilding) => {
    if (!onDealDesk) return;
    setBusy(true);
    setMsg(null);
    try { setMsg(await onDealDesk(f)); } catch (e) { setMsg(e instanceof Error ? e.message : 'Could not send it to the Deal Desk.'); } finally { setBusy(false); }
  };

  const geo = (() => {
    const by = new Map<string, { beds: number; n: number }>();
    for (const f of fac) {
      if (!f.state) continue;
      const b = by.get(f.state) ?? { beds: 0, n: 0 };
      b.beds += num(f.beds) ?? 0;
      b.n += 1;
      by.set(f.state, b);
    }
    const rows = [...by.entries()].sort((a, b) => b[1].beds - a[1].beds);
    return { rows, missing: fac.length - rows.reduce((s, [, b]) => s + b.n, 0), max: Math.max(1, ...rows.map(([, b]) => b.beds)) };
  })();

  return (
    <div data-testid="intel-network" style={sx('display:flex;flex-direction:column;gap:9px;margin-top:6px;')}>
      <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
        <span style={sx('font-size:16px;font-weight:700;color:' + INK + ';')}>Relationship network</span>
        <span style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;color:' + BLUE + ';')}>OWN · CHAIN</span>
        <HonestyBadge flags={[d]} figure="ownership network" />
        <span data-testid="intel-network-count" style={sx('font-size:14px;color:' + INK3 + ';')}>{`${entities} ${entities === 1 ? 'entity' : 'entities'} · ${entities} ${entities === 1 ? 'link' : 'links'}`}</span>
        <span style={sx('flex:1;')} />
        <span role="tablist" aria-label="Network lens" style={sx('display:inline-flex;padding:2px;border-radius:8px;border:1px solid ' + LINE + ';')}>
          {(['Graph', 'Supply Chain', 'Geography'] as Lens[]).map((l) => (
            <a
              key={l} href="#" role="tab" aria-selected={lens === l}
              onClick={(e) => { e.preventDefault(); setLens(l); }}
              style={sx('display:inline-flex;align-items:center;height:24px;padding:0 11px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;' + (lens === l ? 'background:rgba(79,138,214,.15);color:' + BLUE + ';' : 'color:' + INK3 + ';'))}
            >{l}</a>
          ))}
        </span>
      </div>

      <div style={sx('display:flex;gap:12px;flex-wrap:wrap;align-items:stretch;')}>
        <div style={sx('flex:1 1 520px;min-width:0;display:flex;flex-direction:column;border:1px solid ' + LINE + ';border-radius:10px;overflow:hidden;')}>
          <div style={sx('display:flex;gap:7px;flex-wrap:wrap;padding:8px 10px;border-bottom:1px solid rgba(34,39,46,.1);')}>
            {(Object.keys(count) as Cluster[]).map((c) => {
              const n = count[c];
              const isOn = on.has(c);
              return (
                <button
                  key={c} type="button" data-testid={'net-chip-' + c}
                  aria-pressed={isOn}
                  disabled={!live(c) || n == null}
                  title={n == null ? `${c}: no feed yet${c in WAITING_ON ? ': ' + WAITING_ON[c as keyof typeof WAITING_ON] : ''}` : `${c}: ${isOn ? 'shown' : 'hidden'}`}
                  onClick={() => setOn((s) => { const x = new Set(s); if (x.has(c)) x.delete(c); else x.add(c); return x; })}
                  style={sx('display:inline-flex;align-items:center;gap:6px;height:24px;padding:0 10px;border-radius:12px;border:1px solid ' + LINE + ';background:transparent;font:inherit;font-size:14px;font-weight:600;color:' + INK2 + ';cursor:' + (live(c) && n != null ? 'pointer' : 'default') + ';opacity:' + (n == null ? 0.55 : isOn ? 1 : 0.45) + ';' + (live(c) && n != null && !isOn ? 'text-decoration:line-through;' : ''))}
                >
                  <span style={sx('width:8px;height:8px;border-radius:50%;background:' + (c === 'Buildings' ? INK : c === 'Backers' ? BLUE : GREY) + ';')} />
                  {c}
                  <span style={sx('font-variant-numeric:tabular-nums;color:' + INK3 + ';')}>{n == null ? '–' : String(n)}</span>
                </button>
              );
            })}
          </div>

          {lens === 'Graph' && (
            <svg data-testid="intel-network-graph" viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`Ownership network of ${org}`} style={sx('display:block;background:rgba(34,39,46,.025);')}>
              {[0.62, 0.93, 1.24].map((m) => (
                <ellipse key={m} cx={CX} cy={CY} rx={RX * m} ry={RY * m} fill="none" stroke="rgba(34,39,46,.08)" strokeDasharray="3 5" />
              ))}
              {g.nodes.map((n) => (
                <line key={'e' + n.id} data-edge={n.id} x1={CX} y1={CY} x2={n.x} y2={n.y} stroke={n.kind === 'backer' ? BLUE : 'rgba(34,39,46,.35)'} strokeWidth={n.kind === 'backer' ? 1.4 : 1} strokeDasharray={n.kind === 'backer' ? '4 3' : undefined} opacity={hover && hover !== n.id ? 0.25 : 1} />
              ))}
              {g.ghosts.map((x) => (
                <g key={'g' + x.cluster} data-testid={'net-slot-' + x.cluster}>
                  <circle cx={x.x} cy={x.y} r={13} fill="none" stroke={GREY} strokeDasharray="3 3" />
                  <text x={x.x} y={x.y + 27} textAnchor="middle" fontSize={11} fontWeight={700} fill={INK3}>{x.cluster === 'Referrals' && on.has('Peers') && on.has('Referrals') ? 'Referrals · Peers' : x.cluster}</text>
                  <text x={x.x} y={x.y + 41} textAnchor="middle" fontSize={9.5} fill={INK3}>{short('no feed: ' + x.note, 46)}</text>
                </g>
              ))}
              {g.nodes.map((n, i) => (
                <g
                  key={n.id} data-node={n.id} style={{ cursor: 'pointer' }} opacity={hover && hover !== n.id ? 0.4 : 1}
                  onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)}
                  onClick={() => setInspected(inspected === n.id ? null : n.id)}
                >
                  <title>{n.title}</title>
                  {n.kind === 'backer' ? (
                    <rect x={n.x - n.r} y={n.y - n.r} width={n.r * 2} height={n.r * 2} transform={`rotate(45 ${n.x} ${n.y})`} fill={n.fill} />
                  ) : (
                    <circle cx={n.x} cy={n.y} r={n.r} fill={n.fill} />
                  )}
                  {n.ring && <circle cx={n.x} cy={n.y} r={n.r + 4} fill="none" stroke={BLUE} strokeWidth={1.4} />}
                  {n.flag && <circle cx={n.x + n.r * 0.8} cy={n.y - n.r * 0.8} r={3.5} fill={RED} stroke="#fff" />}
                  {inspected === n.id && <circle cx={n.x} cy={n.y} r={n.r + 7} fill="none" stroke={INK} strokeWidth={1.5} />}
                  {(i < 14 || hover === n.id || inspected === n.id) && (
                    <text x={n.x} y={n.y - n.r - 6} textAnchor="middle" fontSize={10} fill={INK2}>{short(n.label)}</text>
                  )}
                </g>
              ))}
              <circle cx={CX} cy={CY} r={26} fill={INK} />
              <text x={CX} y={CY + 4} textAnchor="middle" fontSize={11} fontWeight={700} fill="#fff">{org.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('')}</text>
              <text x={CX} y={CY + 42} textAnchor="middle" fontSize={11.5} fontWeight={700} fill={INK}>{short(org, 34)}</text>
              <text x={CX} y={CY + 56} textAnchor="middle" fontSize={10} fill={INK3}>{`${fac.length} ${fac.length === 1 ? 'building' : 'buildings'}`}</text>
            </svg>
          )}

          {lens === 'Supply Chain' && (
            <div data-testid="intel-network-supply" style={sx('padding:22px 20px;display:flex;flex-direction:column;gap:8px;max-width:560px;align-self:center;')}>
              <span style={sx('font-size:16px;font-weight:700;color:' + INK2 + ';text-align:center;')}>Referral flow is coming soon</span>
              <span style={sx('font-size:16px;color:' + INK3 + ';line-height:1.5;text-align:center;')}>
                This lens is three ranked columns: who supplies this operator, who sends it census and who pays for it. We hold no feed for any of them yet, so nothing is drawn rather than something invented.
              </span>
              {([['SUPPLIES IN', WAITING_ON.Suppliers], ['CENSUS IN', WAITING_ON.Referrals], ['PAID BY', WAITING_ON.Demand]] as [string, string][]).map(([k, v]) => (
                <span key={k} style={sx('display:flex;gap:10px;font-size:14px;color:' + INK2 + ';')}>
                  <span style={sx('width:90px;flex:0 0 90px;' + CAP)}>{k}</span>
                  <span>{v}</span>
                </span>
              ))}
            </div>
          )}

          {lens === 'Geography' && (
            <div data-testid="intel-network-geo" style={sx('padding:14px 16px;display:flex;flex-direction:column;gap:7px;')}>
              <span style={sx(CAP)}>Beds by state</span>
              {!geo.rows.length && <span style={sx('font-size:16px;color:' + INK3 + ';')}>No building on this operator carries a state.</span>}
              {geo.rows.map(([st, b]) => (
                <div key={st} style={sx('display:flex;align-items:center;gap:10px;')}>
                  <span style={sx('width:30px;font-size:14px;font-weight:700;color:' + INK + ';')}>{st}</span>
                  <span style={sx('flex:1;height:14px;border-radius:4px;background:rgba(34,39,46,.06);overflow:hidden;')}>
                    <span style={sx('display:block;height:14px;border-radius:4px;background:' + BLUE + ';width:' + Math.max(2, (b.beds / geo.max) * 100) + '%;')} />
                  </span>
                  <span style={sx('width:130px;text-align:right;font-size:14px;color:' + INK2 + ';font-variant-numeric:tabular-nums;')}>{`${n0(b.beds)} beds · ${b.n}`}</span>
                </div>
              ))}
              {geo.missing > 0 && <span style={sx('font-size:14px;color:' + INK3 + ';')}>{`${geo.missing} ${geo.missing === 1 ? 'building has' : 'buildings have'} no state on the catalog row and ${geo.missing === 1 ? 'is' : 'are'} not placed above.`}</span>}
            </div>
          )}

          <div style={sx('padding:7px 10px;border-top:1px solid rgba(34,39,46,.1);font-size:14px;color:' + INK3 + ';')}>
            {'As of the current CMS record.' + (lens === 'Graph' && fac.length > g.drawn && on.has('Buildings') ? ` The ${g.drawn} largest of ${fac.length} buildings are drawn; every one is listed below.` : '')}
          </div>
        </div>

        <div data-testid="intel-network-rail" style={sx('flex:0 1 300px;min-width:240px;display:flex;flex-direction:column;gap:12px;border:1px solid ' + LINE + ';border-radius:10px;padding:11px 13px;')}>
          <div style={sx('display:flex;align-items:baseline;gap:8px;')}>
            <span style={sx(CAP)}>{sel ? 'Building' : selOwner ? 'Backer' : 'Focal'}</span>
            <span style={sx('flex:1;')} />
            {(sel || selOwner) && <a href="#" onClick={(e) => { e.preventDefault(); setInspected(null); }} style={sx('font-size:14px;color:' + BLUE + ';text-decoration:none;')}>back to operator</a>}
          </div>
          <span style={sx('font-size:16px;font-weight:700;color:' + INK + ';margin-top:-8px;')}>{sel ? sel.name : selOwner ? selOwner.name : org}</span>
          {!sel && !selOwner && (
            <>
              <span style={sx('font-size:14px;color:' + INK3 + ';margin-top:-10px;')}>{`${fac.length} buildings · ${states.length} ${states.length === 1 ? 'state' : 'states'}`}</span>
              <Gauge
                title="Distress" value={distress}
                word={distress == null ? 'NOT SCORED' : distress >= 55 ? 'ELEVATED' : distress >= 40 ? 'WATCH' : 'CONTAINED'}
                color={distress == null ? GREY : distress >= 55 ? RED : distress >= 40 ? AMBER : GREEN}
                rows={[['In trouble', `${distressed} of ${scored.length} scored`], ['PE-backed (name heuristic)', pe === 0 ? 'none' : `${pe} of ${fac.length}`], ['Unscored', String(fac.length - scored.length)]]}
              />
              <Gauge
                title="Concentration" value={hhi == null ? null : hhi / 100}
                word={hhi == null ? 'NO STATE DATA' : hhi >= 5000 ? 'SINGLE-STATE' : hhi >= 1500 ? 'CONCENTRATED' : 'SPREAD'}
                color={hhi == null ? GREY : hhi >= 5000 ? RED : hhi >= 1500 ? AMBER : GREEN}
                rows={[['HHI, beds by state', hhi == null ? '–' : n0(hhi)], ['States', String(states.length)], ['Beds', bedTotal ? n0(bedTotal) : '–']]}
              />
              <div style={sx('display:flex;flex-direction:column;gap:5px;')}>
                <span style={sx(CAP)}>Largest buildings · share of beds</span>
                {[...fac].sort((a, b) => (num(b.beds) ?? 0) - (num(a.beds) ?? 0)).slice(0, 6).map((f) => (
                  <a key={f.id} href="#" onClick={(e) => { e.preventDefault(); setInspected(f.id); }} style={sx('display:flex;align-items:center;gap:8px;font-size:14px;color:' + INK2 + ';text-decoration:none;')}>
                    <span style={sx('width:8px;height:8px;border-radius:50%;flex:0 0 8px;background:' + scoreColor(num(f.composite_score)) + ';')} />
                    <span style={sx('flex:1;min-width:0;overflow-wrap:break-word;')}>{f.name}</span>
                    <span style={sx('font-variant-numeric:tabular-nums;')}>{bedTotal > 0 && num(f.beds) != null ? `${(((num(f.beds) ?? 0) / bedTotal) * 100).toFixed(0)}%` : '–'}</span>
                  </a>
                ))}
              </div>
            </>
          )}
          {selOwner && (
            <div style={sx('display:flex;flex-direction:column;gap:4px;font-size:14px;color:' + INK2 + ';')}>
              <span>{`On ${selOwner.buildings} of ${fac.length} buildings`}</span>
              {selOwner.roles.map((r) => <span key={r}>{r.toLowerCase()}</span>)}
              {selOwner.maxPct != null && <span>{`Up to ${selOwner.maxPct}% held`}</span>}
              {selOwner.peLike && <span>PE / REIT by name (heuristic)</span>}
              <span style={sx('color:' + INK3 + ';')}>From the CMS owner rows of these buildings.</span>
            </div>
          )}
          {sel && (
            <div style={sx('display:flex;flex-direction:column;gap:5px;')}>
              {([
                ['Where', [sel.city, sel.state].filter(Boolean).join(', ') || '–'],
                ['Composite', num(sel.composite_score) == null ? 'not scored' : Math.round(num(sel.composite_score) ?? 0) + ' / 100'],
                ['Grade', sel.score_grade ?? '–'],
                ['Beds', n0(num(sel.beds))],
                ['Share of chain', num(sel.beds) == null || bedTotal <= 0 ? '–' : (((num(sel.beds) ?? 0) / bedTotal) * 100).toFixed(1) + '%'],
                ['PE-backed (name heuristic)', sel.pe_backed == null ? '–' : sel.pe_backed ? 'yes' : 'no'],
                ['Severe deficiencies', sel.severe_deficiency_count == null ? '–' : String(sel.severe_deficiency_count)],
              ] as [string, string][]).map(([k, v]) => (
                <span key={k} style={sx('display:flex;justify-content:space-between;gap:8px;font-size:14px;color:' + INK2 + ';')}>
                  <span>{k}</span>
                  <span style={sx('font-weight:700;color:' + INK + ';')}>{v}</span>
                </span>
              ))}
              {onDealDesk && (
                <button
                  type="button" disabled={busy} onClick={() => void send(sel)}
                  style={sx('margin-top:6px;height:30px;border:0;border-radius:7px;background:' + BLUE + ';color:#fff;font:inherit;font-size:16px;font-weight:700;cursor:pointer;')}
                >{busy ? 'Sending…' : 'Send to Deal Desk'}</button>
              )}
              {msg && <span role="status" style={sx('font-size:14px;color:' + INK3 + ';')}>{msg}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
